import { ProjectPeriodIntervalModel, ProjectPeriodModel, TimeModel } from "app/models/project.model";

export type ProjectPeriodSegment = {
    start: number;
    end: number;
    periodName: string;
};

function toMinutes(hour: number, minute: number): number {
    return hour * 60 + minute;
}

function cloneTime(time: TimeModel): TimeModel {
    return { hour: time.hour, minute: time.minute };
}

function normalizeIntervalEndMinutes(start: number, end: number): number {
    if (start > 0 && end === 0) {
        return 24 * 60;
    }

    return end;
}

function minutesToTime(totalMinutes: number): TimeModel {
    const normalized = ((totalMinutes % (24 * 60)) + 24 * 60) % (24 * 60);
    return {
        hour: Math.floor(normalized / 60),
        minute: normalized % 60,
    };
}

export function formatProjectTime(time: TimeModel | null | undefined): string {
    if (!time) {
        return "--:--";
    }

    return `${time.hour.toString().padStart(2, "0")}:${time.minute.toString().padStart(2, "0")}`;
}

export function getProjectPeriodIntervals(period: ProjectPeriodModel | null | undefined): ProjectPeriodIntervalModel[] {
    if (!period) {
        return [];
    }

    if (period.intervals?.length) {
        return period.intervals.map((interval) => ({
            startTime: cloneTime(interval.startTime),
            endTime: cloneTime(interval.endTime),
        }));
    }

    return [];
}

function normalizeWrappedProjectPeriodIntervals(intervals: ProjectPeriodIntervalModel[]): ProjectPeriodIntervalModel[] {
    return intervals
        .flatMap((interval) => {
            const start = toMinutes(interval.startTime.hour, interval.startTime.minute);
            const rawEnd = toMinutes(interval.endTime.hour, interval.endTime.minute);
            const end = normalizeIntervalEndMinutes(start, rawEnd);

            if (start === end) {
                return [
                    {
                        startTime: minutesToTime(0),
                        endTime: minutesToTime(0),
                    },
                ];
            }

            if (start < end) {
                return [
                    {
                        startTime: cloneTime(interval.startTime),
                        endTime: cloneTime(interval.endTime),
                    },
                ];
            }

            return [
                {
                    startTime: minutesToTime(0),
                    endTime: minutesToTime(rawEnd),
                },
                {
                    startTime: minutesToTime(start),
                    endTime: minutesToTime(0),
                },
            ];
        })
        .sort(
            (left, right) =>
                toMinutes(left.startTime.hour, left.startTime.minute) - toMinutes(right.startTime.hour, right.startTime.minute),
        );
}

export function normalizeProjectPeriod(period: ProjectPeriodModel): ProjectPeriodModel {
    return {
        name: period.name,
        intervals: normalizeWrappedProjectPeriodIntervals(getProjectPeriodIntervals(period)),
    };
}

export function getProjectPeriodSegments(period: ProjectPeriodModel): ProjectPeriodSegment[] {
    return getProjectPeriodIntervals(period).flatMap((interval) => {
        const start = toMinutes(interval.startTime.hour, interval.startTime.minute);
        const end = normalizeIntervalEndMinutes(start, toMinutes(interval.endTime.hour, interval.endTime.minute));

        if (start === end) {
            return [{ start: 0, end: 24 * 60, periodName: period.name }];
        }
        if (start < end) {
            return [{ start, end, periodName: period.name }];
        }

        return [
            { start, end: 24 * 60, periodName: period.name },
            { start: 0, end, periodName: period.name },
        ];
    });
}

export function validateProjectPeriods(periods: ProjectPeriodModel[]): string | null {
    if (!periods?.length) {
        return null;
    }

    const uniqueNames = new Set<string>();
    for (const period of periods) {
        if (uniqueNames.has(period.name)) {
            return "Project period names must be unique";
        }
        uniqueNames.add(period.name);

        if (!getProjectPeriodIntervals(period).length) {
            return "Project periods must define at least one interval";
        }
    }

    const segments = periods
        .flatMap(getProjectPeriodSegments)
        .filter((segment) => segment.end > segment.start)
        .sort((left, right) => left.start - right.start);
    if (!segments.length || segments[0].start !== 0) {
        return "Project periods must cover the full day without gaps";
    }

    let cursor = 0;
    for (const segment of segments) {
        if (segment.start > cursor) {
            return "Project periods must cover the full day without gaps";
        }
        if (segment.start < cursor) {
            return "Project periods cannot overlap";
        }
        cursor = segment.end;
    }

    if (cursor !== 24 * 60) {
        return "Project periods must cover the full day without gaps";
    }

    return null;
}
